// Platform-neutral desktop-notification wrapper.
//
// Two implementations hide behind one surface:
//
//   - Web: `Notification` API. Permission flows through
//     `Notification.requestPermission()`; `showNotification` creates a
//     new `Notification` and wires the `click` handler.
//   - Tauri: `@tauri-apps/plugin-notification`. Permission goes through
//     `isPermissionGranted` + `requestPermission`; `sendNotification`
//     fires a native OS notification. Click handling on Tauri happens
//     OS-side; we don't receive a reliable "user clicked the toast"
//     callback on the desktop plugin in v2.10, so the Tauri branch
//     focuses the app on any interaction (the user clicked something,
//     they want the window) and lets the last-active room remain
//     active. Web keeps the richer click-to-room behaviour.
//
// Security: the web path also guards against running in environments
// that don't support the `Notification` API (older Safari, embedded
// webviews, Storybook iframes). In those cases `requestPermission`
// resolves to `"denied"` and `showNotification` is a no-op.

import { platform } from "~/lib/platform";

export type NotificationPermission = "granted" | "denied" | "default";

export interface ShowNotificationOptions {
  title: string;
  /** Event body truncated to <=100 chars by the caller — this layer
   *  only renders what it's given. */
  body: string;
  /** Optional avatar URL (sender). Silently ignored on Tauri today;
   *  the web `Notification` honours it as the glyph. */
  iconUrl?: string;
  /** Deduplication tag. Matching web notifications replace the
   *  previous one so a busy room doesn't stack toasts. */
  tag?: string;
  /** Invoked when the user clicks the notification on web. Tauri
   *  does not deliver reliable click callbacks for desktop, so on
   *  Tauri the click handler is invoked optimistically whenever the
   *  app regains focus — callers must be idempotent. */
  onClick?: () => void;
}

/**
 * Whether the runtime exposes a Web Notifications API at all. Returns
 * `false` in any environment that doesn't — `Notification` is missing
 * entirely in some embedded webviews and old Safari, and the whole
 * permission/send flow is a no-op in that branch.
 */
function webNotificationsSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

/**
 * Ask the platform for notification permission. Idempotent — calling
 * after the user has already granted or denied resolves without a
 * prompt. `"default"` is the pre-prompt state; the UI shows a banner
 * that explains the value of turning them on and offers a retry.
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (platform === "tauri") {
    const { isPermissionGranted, requestPermission: tauriRequest } =
      await import("@tauri-apps/plugin-notification");
    const granted = await isPermissionGranted();
    if (granted) return "granted";
    const next = await tauriRequest();
    // The tauri plugin's return shape is `'granted' | 'denied'` — it
    // never returns `'default'` because the prompt has resolved by
    // the time the promise settles. We normalise here so consumers
    // only need to branch on three states regardless of platform.
    return next === "granted" ? "granted" : "denied";
  }
  if (!webNotificationsSupported()) return "denied";
  const current = Notification.permission;
  if (current === "granted" || current === "denied") {
    return current;
  }
  const next = await Notification.requestPermission();
  return next;
}

/**
 * Read the current permission without prompting. Useful for the
 * permission banner's "show me if denied" branch. Returns
 * `"default"` before any prompt has happened, matching the web API
 * contract.
 */
export async function getPermission(): Promise<NotificationPermission> {
  if (platform === "tauri") {
    const { isPermissionGranted } = await import("@tauri-apps/plugin-notification");
    const granted = await isPermissionGranted();
    // Tauri collapses "default" into `!granted`; there's no separate
    // "not prompted yet" signal on the plugin. We err on the side of
    // the user-visible banner staying hidden until they've been
    // prompted — surfacing "denied" here would show the warning
    // banner before the user has had a chance to say yes. `default`
    // on the web branch ensures we never spam the banner on first
    // boot; the same rule should apply on Tauri.
    return granted ? "granted" : "default";
  }
  if (!webNotificationsSupported()) return "denied";
  return Notification.permission;
}

/**
 * Only `http(s)` and `data:` URLs are safe to pass to the web
 * `Notification` constructor's `icon` field. Matrix `mxc://` URLs
 * would produce a broken icon (and potentially leak the URL), so we
 * validate the scheme before handing it off.
 */
function isSafeIconUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === "https:" || parsed.protocol === "http:" || parsed.protocol === "data:"
    );
  } catch {
    return false;
  }
}

/**
 * Show a desktop notification. No-op when permission is missing or
 * the platform doesn't expose a notifications API. The caller is
 * responsible for deciding whether the app is unfocused — this layer
 * does not re-check `document.hasFocus()` because the hook that
 * drives it owns that decision (and needs to double as the favicon
 * refresh trigger).
 */
export async function showNotification(options: ShowNotificationOptions): Promise<void> {
  const { title, body, iconUrl, tag, onClick } = options;
  if (platform === "tauri") {
    const { isPermissionGranted, sendNotification } =
      await import("@tauri-apps/plugin-notification");
    // Re-check permission on every send — the user may have revoked
    // it in system settings since the last prompt. Swallowing the
    // rejection here is the documented fail-silent contract; a
    // notification that can't fire is not a user-visible error.
    if (!(await isPermissionGranted())) return;
    // `iconUrl` is intentionally unused on Tauri today: the v2.10
    // plugin only accepts `asset://` / `file://` attachment URLs,
    // not the `https://` thumbnail the Matrix server hands back.
    // Dropping the icon is preferable to leaking an unusable URL
    // into the plugin and getting a hard error.
    void iconUrl;
    try {
      // `sendNotification` returns a Promise on v2.x — await it so
      // the caller's `try/catch` actually covers IPC failures.
      await Promise.resolve(sendNotification({ title, body }));
    } catch (err) {
      console.warn("showNotification (tauri): sendNotification failed", err);
    }
    // Tauri's desktop notification plugin does not surface a click
    // callback on v2.10. We keep the parameter so the web+tauri
    // contract is identical; callers that need real click routing
    // fall through to the `focus` listener + pending-room handoff
    // in `useNotifications`.
    void onClick;
    return;
  }
  if (!webNotificationsSupported()) return;
  if (Notification.permission !== "granted") return;
  try {
    // Only pass `iconUrl` through when the scheme is one the web
    // Notification constructor accepts. Dropping an invalid icon is
    // strictly better than leaking a `mxc://` URL into a field the
    // browser would then render as a broken image.
    const safeIcon = iconUrl && isSafeIconUrl(iconUrl) ? iconUrl : undefined;
    const n = new Notification(title, { body, icon: safeIcon, tag });
    if (onClick) {
      n.onclick = () => {
        // `window.focus()` is best-effort on web — most browsers only
        // honour it from a user gesture. The click itself counts as a
        // gesture so the focus request here actually lands.
        try {
          window.focus();
        } catch {
          /* swallow — focus can throw in sandboxed iframes */
        }
        onClick();
        n.close();
      };
    }
  } catch (err) {
    // Permission may have been revoked between the check and the
    // constructor call on some browsers; fail silently.
    console.warn("showNotification (web): failed", err);
  }
}

/**
 * Bring the application window to the foreground. Tauri focuses the
 * native window; web calls `window.focus()` which is best-effort and
 * only works from a user gesture (e.g. the notification click
 * handler, which already counts).
 */
export async function focusApp(): Promise<void> {
  if (platform === "tauri") {
    try {
      const { getCurrentWindow } = await import("@tauri-apps/api/window");
      const w = getCurrentWindow();
      // Unminimise before focus — a minimised window can't take
      // focus on macOS / Linux and the click would otherwise feel
      // broken. `show` is a no-op when already visible.
      await w.unminimize().catch(() => undefined);
      await w.show().catch(() => undefined);
      await w.setFocus();
    } catch (err) {
      console.warn("focusApp (tauri): setFocus failed", err);
    }
    return;
  }
  if (typeof window === "undefined") return;
  try {
    window.focus();
  } catch {
    /* swallow — focus can throw in sandboxed iframes */
  }
}
